<template>
  <Transition name="fade">
    <div v-if="visible" class="start-mask">
      <main class="start-content" aria-live="polite">
        <img src="/img/logo_aiiifly.png" alt="Aiiifly" class="logo-img" />
        <h1 class="slogan">{{ t('start.slogan') }}</h1>
        <p class="instruction">{{ instructionText }}</p>

        <button
          type="button"
          class="start-button"
          :class="buttonState"
          :disabled="isWaiting || deviceStore.isConnecting || isLoadingHome"
          @click="handleStart"
        >
          <span v-if="isLoadingHome" class="loading-spinner" aria-hidden="true"></span>
          <img v-else src="/img/start_Btn_icon.png" alt="" class="start-btn-icon" />
          <span>{{ buttonText }}</span>
        </button>

        <div class="status-tip">{{ statusText }}</div>
      </main>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { useI18n } from 'vue-i18n'

const deviceStore = useDeviceStore()
const { t } = useI18n()
const showConnectedTransition = ref(false)
let connectedTimer: ReturnType<typeof setTimeout> | null = null

const eqPreview = import.meta.env.DEV
  && new URLSearchParams(window.location.search).has('eqPreview')

watch(() => deviceStore.connectionStage, stage => {
  if (stage !== 'connected') return
  showConnectedTransition.value = true
  if (connectedTimer) clearTimeout(connectedTimer)
  connectedTimer = setTimeout(() => {
    showConnectedTransition.value = false
  }, 900)
})

const connectionStage = computed(() => deviceStore.connectionStage)
const isWaiting = computed(() => connectionStage.value === 'waiting_pairing' || connectionStage.value === 'waiting_usb')
const isLoadingHome = computed(() => connectionStage.value === 'connected' && showConnectedTransition.value)
const visible = computed(() => {
  if (eqPreview) return false
  return !deviceStore.isDeviceConnected || isLoadingHome.value
})
const buttonState = computed(() => ({
  'is-active': connectionStage.value === 'initial' || connectionStage.value === 'error',
  'is-error': connectionStage.value !== 'initial' && !isWaiting.value && !isLoadingHome.value,
  'is-waiting': isWaiting.value,
}))
const instructionText = computed(() =>
  connectionStage.value === 'initial'
    ? t('start.tip')
    : t('start.connectHint'),
)
const buttonText = computed(() => {
  if (isLoadingHome.value) return t('start.loadingHome')
  return isWaiting.value ? t('start.waitingDevice') : t('start.use')
})
const statusText = computed(() => {
  if (isLoadingHome.value) return t('start.deviceConnected')
  if (isWaiting.value) return t('start.connectorFound')
  return t('start.noConnector')
})

async function handleStart() {
  if (isWaiting.value || deviceStore.isConnecting || isLoadingHome.value) return
  try {
    await deviceStore.connect()
  } catch (error) {
    console.error('连接失败:', error)
  }
}
</script>

<style scoped>
.start-mask {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #0a0a0a;
  color: #f1f1f1;
  font-family: "Lato", Arial, sans-serif;
}

.start-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100% - 32px, 760px);
}

.logo-img {
  display: block;
  width: 64px;
  height: 64px;
  object-fit: contain;
  margin-bottom: 45px;
}

.slogan {
  margin: 0;
  color: rgba(241, 241, 241, 0.9);
  font-size: 36px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0;
  text-align: center;
}

.instruction {
  margin: 36px 0 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
}

.start-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 180px;
  height: 56px;
  margin-top: 54px;
  padding: 0 28px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  color: #f1f1f1;
  font: inherit;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.start-button.is-active:hover {
  border-color: rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.15);
}

.start-button.is-active:active { background: rgba(255, 255, 255, 0.2); }

.start-button.is-error {
  border-color: transparent;
  background: #376bed;
}

.start-button.is-error:hover { background: #4779ed; }

.start-button.is-waiting,
.start-button:disabled {
  border-color: transparent;
  background: #91acef;
  color: rgba(255, 255, 255, 0.9);
  cursor: default;
}

.start-btn-icon { width: 20px; height: 20px; }

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px dotted rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

.status-tip {
  min-height: 40px;
  margin-top: 36px;
  padding: 10px 24px;
  border: 1px solid rgba(28, 28, 28, 0.8);
  border-radius: 16px;
  background: rgba(28, 28, 28, 0.8);
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  white-space: nowrap;
}

@keyframes spin { to { transform: rotate(360deg); } }

.fade-enter-active,
.fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

@media (min-width: 1401px) {
  .logo-img { width: 72px; height: 72px; margin-bottom: 46px; }
  .slogan { font-size: 48px; }
  .instruction { margin-top: 36px; font-size: 18px; }
  .start-button { min-width: 180px; height: 56px; margin-top: 54px; font-size: 18px; }
  .status-tip { min-width: 518px; margin-top: 36px; }
}

@media (max-width: 1400px) {
  .start-content { transform: translateY(32px); }
  .logo-img { width: 48px; height: 48px; margin-bottom: 51px; }
  .instruction { margin-top: 29px; }
  .start-button { min-width: 164px; height: 52px; margin-top: 40px; font-size: 18px; }
  .status-tip { margin-top: 28px; }
}

@media (max-width: 768px) {
  .start-content { transform: translateY(18px); }
  .logo-img { width: 58px; height: 58px; margin-bottom: 32px; }
  .slogan { font-size: 28px; }
  .instruction { max-width: 90vw; font-size: 14px; }
  .start-button { min-width: 164px; height: 52px; margin-top: 40px; font-size: 18px; }
  .status-tip { max-width: 90vw; white-space: normal; }
}
</style>
